import { useState, useEffect } from 'react';
import {
	setGridEnabled,
	getGridEnabled,
	setGridInterval,
	getGridInterval,
} from '../libs/BitmapEditor/bitmapMaker3';
import { useForceUpdate } from '../hooks/forceUpdate';
import styled from 'styled-components';
import { IconGrid4x4 } from '@tabler/icons-react';

const GRID_INTERVALS = [1, 5, 10, 20];

export default function ToolsGrid() {
	const [gridEnabled, setGridEnabledState] = useState(false);
	const [gridInterval, setGridIntervalLocal] = useState(10);
	const [customInput, setCustomInput] = useState('');
	const handleForceUpdate = useForceUpdate();

	// Initialize grid state from bitmapMaker3
	useEffect(() => {
		setGridEnabledState(getGridEnabled());
		setGridIntervalLocal(getGridInterval());
	}, []);

	const toggleGrid = () => {
		const newGridState = !gridEnabled;
		setGridEnabledState(newGridState);
		setGridEnabled(newGridState);
		// Force canvas to re-render
		handleForceUpdate();
	};

	const handleIntervalChange = (value: number) => {
		setGridIntervalLocal(value);
		setGridInterval(value);
		handleForceUpdate();
	};

	const applyCustomInterval = () => {
		const val = parseInt(customInput, 10);
		if (!isNaN(val) && val >= 1 && val <= 1000) {
			handleIntervalChange(val);
		}
		setCustomInput('');
	};

	return (
		<GridContainer>
			<TopRow>
				<GridToggleButton onClick={toggleGrid} enabled={gridEnabled}>
					<IconGrid4x4 stroke={2} size={16} color={'white'} />
					{gridEnabled ? 'Hide Grid' : 'Show Grid'}
				</GridToggleButton>
			</TopRow>

			{gridEnabled && (
				<IntervalRow>
					<IntervalLabel>Grid spacing:</IntervalLabel>
					{GRID_INTERVALS.map(v => (
						<IntervalButton
							key={v}
							active={gridInterval === v}
							onClick={() => handleIntervalChange(v)}
							title={`Grid every ${v} pixels`}
						>
							{v}
						</IntervalButton>
					))}
					<CustomInput
						type="number"
						min={1}
						max={1000}
						placeholder={String(gridInterval)}
						value={customInput}
						active={!GRID_INTERVALS.includes(gridInterval)}
						onChange={e => setCustomInput(e.target.value)}
						onBlur={applyCustomInterval}
						onKeyDown={e => {
							if (e.key === 'Enter') {
								e.currentTarget.blur();
							}
						}}
						title="Type a custom grid spacing"
					/>
				</IntervalRow>
			)}
		</GridContainer>
	);
}

const GridContainer = styled.div`
	display: flex;
	flex-direction: column;
	width: 100%;
	gap: 8px;
`;

const TopRow = styled.div`
	display: flex;
	align-items: center;
	justify-content: flex-start;
	width: 100%;
	gap: 10px;
`;

const IntervalRow = styled.div`
	display: flex;
	align-items: center;
	justify-content: flex-start;
	flex-wrap: wrap;
	gap: 6px;
`;

const IntervalLabel = styled.span`
	color: #aaa;
	font-size: 11px;
	margin-right: 4px;
`;

const IntervalButton = styled.button.withConfig({
	shouldForwardProp: prop => prop !== 'active',
})<{ active: boolean }>`
	padding: 4px 8px;
	min-width: 30px;
	border-radius: 6px;
	border: 1px solid ${props => (props.active ? '#e5b800' : '#555')};
	background-color: ${props => (props.active ? '#e5b800' : 'transparent')};
	color: ${props => (props.active ? 'white' : '#aaa')};
	font-size: 12px;
	font-weight: ${props => (props.active ? 'bold' : 'normal')};
	cursor: pointer;
	transition: all 0.15s;

	&:hover {
		background-color: ${props => (props.active ? '#e5b800' : '#444')};
	}

	&:active {
		transform: scale(0.95);
	}
`;

const CustomInput = styled.input.withConfig({
	shouldForwardProp: prop => prop !== 'active',
})<{ active: boolean }>`
	width: 50px;
	padding: 4px 8px;
	border-radius: 6px;
	border: 1px solid ${props => (props.active ? '#e5b800' : '#555')};
	background-color: ${props => (props.active ? '#e5b800' : 'transparent')};
	color: ${props => (props.active ? 'white' : '#ddd')};
	font-size: 12px;
	font-weight: ${props => (props.active ? 'bold' : 'normal')};
	text-align: center;
	-moz-appearance: textfield;

	&::-webkit-outer-spin-button,
	&::-webkit-inner-spin-button {
		-webkit-appearance: none;
		margin: 0;
	}

	&::placeholder {
		color: #777;
	}

	&:focus {
		outline: none;
		border-color: #e5b800;
	}
`;

const GridToggleButton = styled.button.withConfig({
	shouldForwardProp: prop => prop !== 'enabled',
})<{ enabled: boolean }>`
	padding: 8px 16px;
	border-radius: 8px;
	border: none;
	background-color: ${props => (props.enabled ? '#e5b800' : '#666')};
	color: white;
	font-size: 12px;
	font-weight: bold;
	cursor: pointer;
	transition: background-color 0.2s;
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 8px;

	&:hover {
		background-color: ${props => (props.enabled ? '#e5b800' : '#555')};
	}

	&:active {
		transform: scale(0.98);
	}
`;
